/**
 * TreeCard - Individual Tree Recommendation Card
 *
 * Displays a recommended tree with image, name, price, and match reasons.
 */

import React from 'react';
import { TreeScore } from '../../utils/citrusQuizLogic';

interface TreeCardProps {
  treeScore: TreeScore;
  rank?: number;
  onOpenVideo?: (videoLink: string) => void;
}

const TreeCard: React.FC<TreeCardProps> = ({ treeScore, rank, onOpenVideo }) => {
  const { tree, matchReasons, personalizedReason } = treeScore;
  const rating = tree.rating ?? (tree.reviews > 0 ? 5 : null);
  const filledStars = rating ? Math.round(rating) : 0;
  const videoLink = tree.videoLink;

  return (
    <div className="tree-card">
      {rank && rank <= 3 && (
        <div className={`tree-card-badge tree-card-badge-${rank}`}>
          {rank === 1 ? '🥇 #1 Match' : rank === 2 ? '🥈 #2 Match' : '🥉 #3 Match'}
        </div>
      )}

      <div className="tree-card-image-container">
        {tree.imageUrl ? (
          <img
            src={tree.imageUrl}
            alt={tree.name}
            className="tree-card-image"
            loading="lazy"
          />
        ) : (
          <div className="tree-card-image tree-card-image-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="tree-card-content">
        <h3 className="tree-card-name">{tree.name}</h3>

        <div className="tree-card-reviews">
          <div className="tree-card-stars" aria-label={rating ? `${rating.toFixed(2)} out of 5 stars` : 'No star rating available'}>
            {[1, 2, 3, 4, 5].map((star) => (
              <span key={star} className={`star ${star <= filledStars ? 'filled' : ''}`}>★</span>
            ))}
          </div>
          {rating && (
            <span className="tree-card-review-rating">{rating.toFixed(2)}/5</span>
          )}
          {tree.reviews > 0 && (
            <span className="tree-card-review-count">{tree.reviews} reviews</span>
          )}
        </div>

        <p className="tree-card-why">{personalizedReason}</p>
        <p className="tree-card-description">{tree.shortDescription}</p>

        <div className="tree-card-match-reasons">
          {matchReasons.slice(0, 3).map((reason, index) => (
            <span key={index} className="match-reason-tag">
              ✓ {reason}
            </span>
          ))}
        </div>

        <div className="tree-card-footer">
          {/* <span className="tree-card-price">
            ${tree.price.toFixed(2)}
          </span> */}

          {videoLink && onOpenVideo ? (
            <button
              type="button"
              className="tree-card__video-btn tree-card-shop-btn"
              onClick={() => onOpenVideo(videoLink)}
            >
              Watch Tree Story
            </button>
          ) : (
            // Native <a> tag guarantees new-tab behavior even inside iframes,
            // unlike window.open which popup blockers can intercept.
            <a
              href={tree.productUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="tree-card-shop-btn"
            >
              Learn More →
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default TreeCard;
